import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../API';
import { regionRefKey, regionRefsEqual, toRegionRef } from '../definition/regionIdentity';
import { useBlockMutations } from './hooks/useBlockMutations';
import { useRegionMutations } from './hooks/useRegionMutations';
import {
    VIEW_MEMBERSHIP,
    VIEW_REGION_DETAILS,
    blockSheetNavReducer,
    createInitialSheetNav,
} from './hooks/useBlockSheetNav';
import { isCaseScope } from './utils/blockScope';
import { regionDisplayName } from './utils/formatRegion';
import CaseScopeBanner from './CaseScopeBanner';
import ConfirmDestructiveDialog from './ConfirmDestructiveDialog';
import BlockMembershipView from './views/BlockMembershipView';
import RegionDetailsSubview from './views/RegionDetailsSubview';
import styles from './blocks.module.css';

/**
 * Side sheet chrome + view router (membership | region details).
 *
 * mode: 'add' | 'edit'
 * initialView: 'membership' | 'regionDetails' (chip click opens on details)
 */
export default function BlockEditorSheet({
    open,
    mode = 'add',
    block = null,
    region = null,
    initialView = VIEW_MEMBERSHIP,
    scope,
    protocolName = null,
    onClose,
    onAffectedItems,
}) {
    const mutations = useBlockMutations(scope);
    const { saveRegion } = useRegionMutations(scope);
    const caseScoped = isCaseScope(scope);

    const [nav, dispatchNav] = useReducer(
        blockSheetNavReducer,
        createInitialSheetNav(initialView, region)
    );
    const [name, setName] = useState('');
    /** Last persisted block name (sheet.block can be stale after rename). */
    const [persistedName, setPersistedName] = useState('');
    const [filenamePattern, setFilenamePattern] = useState('');
    const [persistedFilenamePattern, setPersistedFilenamePattern] = useState('');
    const [patternSavePending, setPatternSavePending] = useState(false);
    const [selectedRegionRefs, setSelectedRegionRefs] = useState([]);
    const [regionDetailsByRefKey, setRegionDetailsByRefKey] = useState({});
    const [error, setError] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [pending, setPending] = useState(false);
    const [nameSavePending, setNameSavePending] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showDiscardNameConfirm, setShowDiscardNameConfirm] = useState(false);
    const [showDiscardRegionConfirm, setShowDiscardRegionConfirm] = useState(null); // 'back' | 'close'
    const [regionDirty, setRegionDirty] = useState(false);
    const [pendingRemove, setPendingRemove] = useState(null);
    const [highlightSchemaKey, setHighlightSchemaKey] = useState(null);
    const regionActionsRef = useRef(null);

    useEffect(() => {
        if (nav.view !== VIEW_REGION_DETAILS) setRegionDirty(false);
    }, [nav.view]);

    const isEditMode = mode === 'edit' && !!block?._id;
    const nameDirty = isEditMode && (name || '').trim() !== (persistedName || '').trim();
    const filenamePatternDirty = isEditMode
        && (filenamePattern || '').trim() !== (persistedFilenamePattern || '').trim();

    const { data: blockRegionsResponse } = useQuery({
        queryKey: ['bdsa-region', 'block', block?._id],
        queryFn: () => api.get('bdsa/region', { query: { blockId: String(block._id) } }),
        staleTime: 5 * 60 * 1000,
        enabled: open && isEditMode && !!block?._id,
    });

    const blockRegions = useMemo(() => {
        if (blockRegionsResponse?.status === 200 && Array.isArray(blockRegionsResponse.data)) {
            return blockRegionsResponse.data;
        }
        return Array.isArray(block?.regionDefinitions) ? block.regionDefinitions : [];
    }, [blockRegionsResponse, block?.regionDefinitions]);

    useEffect(() => {
        if (!open) return;
        setError(null);
        setStatusMessage(null);
        setPending(false);
        setNameSavePending(false);
        setShowDeleteConfirm(false);
        setShowDiscardNameConfirm(false);
        setPendingRemove(null);
        setHighlightSchemaKey(null);
        dispatchNav({
            type: 'reset',
            view: initialView === VIEW_REGION_DETAILS ? VIEW_REGION_DETAILS : VIEW_MEMBERSHIP,
            focusRegion: initialView === VIEW_REGION_DETAILS ? region : null,
        });
        if (mode === 'add') {
            setName('');
            setPersistedName('');
            setFilenamePattern('');
            setPersistedFilenamePattern('');
            setSelectedRegionRefs([]);
            setRegionDetailsByRefKey({});
        } else if (mode === 'edit' && block) {
            const initial = block.name || '';
            setName(initial);
            setPersistedName(initial);
            const patternInitial = block.filenamePattern || '';
            setFilenamePattern(patternInitial);
            setPersistedFilenamePattern(patternInitial);
            setSelectedRegionRefs([]);
            setRegionDetailsByRefKey({});
        }
        // Intentionally key off identity fields so parent object churn does not reset mid-edit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mode, block?._id, initialView, region?._id]);

    if (!open) return null;

    const displayBlockName = isEditMode ? (persistedName || block?.name || '') : (name || '');
    const title = isEditMode
        ? (caseScoped ? `Edit case block ${displayBlockName}` : `Edit block ${displayBlockName}`)
        : (caseScoped ? 'Add case block' : 'Add block');

    const showStatus = (message) => {
        setStatusMessage(message);
        window.setTimeout(() => setStatusMessage(null), 3500);
    };

    const handleToggleRegion = async (regionOption) => {
        setError(null);
        if (isEditMode && block?._id) {
            const targetSchemaKey = regionOption?.schemaRegionKey ?? null;
            const blockRegion = targetSchemaKey
                ? blockRegions.find((br) => br.schemaRegionKey === targetSchemaKey)
                : null;

            if (blockRegion) {
                const itemCount = block?.itemCount ?? 0;
                if (itemCount > 0) {
                    setPendingRemove(blockRegion);
                    return;
                }
                await removeRegionFromBlock(blockRegion);
                return;
            }

            setPending(true);
            try {
                const created = await mutations.addRegionsToBlock(block._id, regionOption._id
                    ? { regionIds: [regionOption._id] }
                    : { schemaRegionKeys: [regionOption.schemaRegionKey] });
                const createdList = Array.isArray(created) ? created : [];
                showStatus(caseScoped ? 'Added to this case’s block' : 'Added to this block');
                if (targetSchemaKey) {
                    setHighlightSchemaKey(targetSchemaKey);
                }
                if (createdList[0]?.schemaRegionKey) {
                    setHighlightSchemaKey(createdList[0].schemaRegionKey);
                }
            } catch (err) {
                setError(err?.message || 'Failed to update regions');
            } finally {
                setPending(false);
            }
            return;
        }

        const ref = regionOption._id
            ? toRegionRef(regionOption, 'template')
            : toRegionRef(regionOption, 'schema');
        if (!ref) return;
        const isSelected = selectedRegionRefs.some((r) => regionRefsEqual(r, ref));
        if (isSelected) {
            setSelectedRegionRefs((prev) => prev.filter((r) => !regionRefsEqual(r, ref)));
        } else {
            setSelectedRegionRefs((prev) => [...prev, ref]);
            setHighlightSchemaKey(regionOption.schemaRegionKey || null);
        }
    };

    const removeRegionFromBlock = async (blockRegion) => {
        setPending(true);
        setError(null);
        try {
            await mutations.removeRegionFromBlock(blockRegion._id, block._id);
            showStatus(caseScoped ? 'Removed from this case’s block' : 'Removed from this block');
        } catch (err) {
            setError(err?.message || 'Failed to remove region');
        } finally {
            setPending(false);
            setPendingRemove(null);
        }
    };

    const handleConfigureRegion = (regionDoc) => {
        if (!regionDoc) return;
        // Draft (create mode): merge stored details
        if (regionDoc.__draftRef) {
            const key = regionRefKey(regionDoc.__draftRef);
            dispatchNav({
                type: 'showRegionDetails',
                region: {
                    ...regionDoc,
                    details: regionDetailsByRefKey[key] || regionDoc.details || {},
                },
            });
            return;
        }
        dispatchNav({ type: 'showRegionDetails', region: regionDoc });
    };

    const handleSaveRegionDetails = async (modified) => {
        const focus = nav.focusRegion;
        if (focus?.__draftRef) {
            const key = regionRefKey(focus.__draftRef);
            setRegionDetailsByRefKey((prev) => ({
                ...prev,
                [key]: modified?.details ?? {},
            }));
            setRegionDirty(false);
            dispatchNav({ type: 'showMembership' });
            return;
        }
        setPending(true);
        setError(null);
        try {
            await saveRegion(modified);
            setRegionDirty(false);
            dispatchNav({ type: 'showMembership' });
            showStatus('Saved region details');
        } catch (err) {
            setError(err?.message || 'Failed to save region');
        } finally {
            setPending(false);
        }
    };

    const leaveRegionDetails = () => {
        setShowDiscardRegionConfirm(null);
        setRegionDirty(false);
        dispatchNav({ type: 'showMembership' });
    };

    const requestLeaveRegion = () => {
        if (regionDirty) {
            setShowDiscardRegionConfirm('back');
            return;
        }
        leaveRegionDetails();
    };

    const handleSaveBlockName = async () => {
        if (!isEditMode || !nameDirty) return;
        const trimmed = (name || '').trim();
        if (!trimmed) {
            setError('Block name is required');
            return;
        }
        setPending(true);
        setNameSavePending(true);
        setError(null);
        try {
            const protocolId = caseScoped
                ? (scope.doc?.protocolId ?? null)
                : scope.protocolId;
            await mutations.updateBlockName({
                blockId: block._id,
                name: trimmed,
                protocolId,
            });
            setName(trimmed);
            setPersistedName(trimmed);
            showStatus('Saved');
        } catch (err) {
            setError(err?.message || 'Failed to save block name');
        } finally {
            setPending(false);
            setNameSavePending(false);
        }
    };

    const handleSaveBlockPattern = async () => {
        if (!isEditMode || !filenamePatternDirty) return;
        setPending(true);
        setPatternSavePending(true);
        setError(null);
        try {
            const protocolId = caseScoped
                ? (scope.doc?.protocolId ?? null)
                : scope.protocolId;
            await mutations.updateBlockPattern({
                blockId: block._id,
                filenamePattern: (filenamePattern || '').trim(),
                protocolId,
            });
            const trimmed = (filenamePattern || '').trim();
            setFilenamePattern(trimmed);
            setPersistedFilenamePattern(trimmed);
            showStatus('Saved pattern');
        } catch (err) {
            setError(err?.message || 'Failed to save filename pattern');
        } finally {
            setPending(false);
            setPatternSavePending(false);
        }
    };

    const handleCreateBlock = async () => {
        if (isEditMode) return;
        const trimmed = (name || '').trim();
        if (!trimmed) {
            setError('Block name is required');
            return;
        }
        if (selectedRegionRefs.length === 0) {
            setError('Please select at least one region');
            return;
        }
        setPending(true);
        setError(null);
        try {
            const templateRefs = selectedRegionRefs.filter((r) => r?.kind === 'template');
            const schemaRefs = selectedRegionRefs.filter((r) => r?.kind === 'schema');
            const regionIds = templateRefs.map((r) => r.id);
            const schemaRegionKeys = schemaRefs.map((r) => r.key);
            const regionDetails = [
                ...templateRefs.map((ref) => regionDetailsByRefKey[regionRefKey(ref)] ?? {}),
                ...schemaRefs.map((ref) => regionDetailsByRefKey[regionRefKey(ref)] ?? {}),
            ];
            await mutations.createBlock({
                name: trimmed,
                regionIds,
                schemaRegionKeys,
                regionDetails,
                filenamePattern: (filenamePattern || '').trim() || undefined,
            });
            onClose?.();
        } catch (err) {
            setError(err?.message || 'Failed to create block');
        } finally {
            setPending(false);
        }
    };

    const handleDelete = async () => {
        setShowDeleteConfirm(false);
        setPending(true);
        setError(null);
        try {
            const response = await mutations.deleteBlock(block._id);
            const affected = response?.data?.affectedItemIds;
            if (Array.isArray(affected) && affected.length > 0) {
                onAffectedItems?.(affected);
            }
            onClose?.();
        } catch (err) {
            setError(err?.message || 'Failed to delete block');
        } finally {
            setPending(false);
        }
    };

    const requestClose = () => {
        if (nav.view === VIEW_REGION_DETAILS && regionDirty) {
            setShowDiscardRegionConfirm('close');
            return;
        }
        if (nameDirty) {
            setShowDiscardNameConfirm(true);
            return;
        }
        onClose?.();
    };

    const handleDiscardNameAndClose = () => {
        setShowDiscardNameConfirm(false);
        onClose?.();
    };

    const confirmDiscardRegion = () => {
        const next = showDiscardRegionConfirm;
        leaveRegionDetails();
        if (next === 'close') onClose?.();
    };

    const onRegionDetails = nav.view === VIEW_REGION_DETAILS && nav.focusRegion;
    const sheetTitle = onRegionDetails
        ? (regionDisplayName(nav.focusRegion) || 'Region details')
        : title;

    return (
        <>
            <div className={styles.sheetOverlay} onClick={requestClose} aria-hidden="true" />
            <aside className={styles.sheet} role="dialog" aria-modal="true" aria-label={sheetTitle}>
                <div className={styles.sheetHeader}>
                    <div className={styles.sheetHeaderText}>
                        <h3 className={styles.sheetTitle}>{onRegionDetails ? 'Region details' : title}</h3>
                        {caseScoped && !onRegionDetails && (
                            <CaseScopeBanner
                                protocolName={protocolName}
                                hasProtocol={!!(protocolName || scope.doc?.protocolId)}
                                compact
                            />
                        )}
                    </div>
                    <button type="button" className={styles.linkButton} onClick={requestClose}>
                        {isEditMode ? 'Done' : 'Cancel'}
                    </button>
                </div>

                <div className={styles.sheetBody}>
                    {error && <div className={styles.errorBanner}>{error}</div>}

                    {onRegionDetails ? (
                        <RegionDetailsSubview
                            region={nav.focusRegion}
                            blockName={isEditMode ? displayBlockName : (name || null)}
                            scope="block"
                            caseScoped={caseScoped}
                            onBack={requestLeaveRegion}
                            onSave={handleSaveRegionDetails}
                            onDirtyChange={setRegionDirty}
                            actionsRef={regionActionsRef}
                        />
                    ) : (
                        <BlockMembershipView
                            name={name}
                            onNameChange={setName}
                            isEditMode={isEditMode}
                            blockName={isEditMode ? displayBlockName : name}
                            caseScoped={caseScoped}
                            selectedRegionRefs={selectedRegionRefs}
                            blockRegions={blockRegions}
                            onToggleRegion={handleToggleRegion}
                            onConfigureRegion={handleConfigureRegion}
                            disabled={pending}
                            statusMessage={statusMessage}
                            highlightSchemaKey={highlightSchemaKey}
                            nameDirty={nameDirty}
                            onSaveName={handleSaveBlockName}
                            saveNamePending={nameSavePending}
                            filenamePattern={filenamePattern}
                            onFilenamePatternChange={setFilenamePattern}
                            onSaveFilenamePattern={handleSaveBlockPattern}
                            filenamePatternDirty={filenamePatternDirty}
                            savePatternPending={patternSavePending}
                            currentCaseNetworkId={caseScoped ? (scope?.caseId || null) : null}
                        />
                    )}
                </div>

                {onRegionDetails ? (
                    <div className={styles.sheetFooter}>
                        <button
                            type="button"
                            className={styles.buttonSecondary}
                            onClick={requestLeaveRegion}
                            disabled={pending}
                        >
                            {regionDirty ? 'Discard' : 'Cancel'}
                        </button>
                        <button
                            type="button"
                            className={styles.buttonPrimary}
                            onClick={() => regionActionsRef.current?.save?.()}
                            disabled={pending}
                        >
                            {pending ? 'Saving…' : 'Save region details'}
                        </button>
                    </div>
                ) : (
                    <div className={styles.sheetFooter}>
                        {isEditMode && (
                            <button
                                type="button"
                                className={styles.buttonDanger}
                                onClick={() => setShowDeleteConfirm(true)}
                                disabled={pending}
                                style={{ marginRight: 'auto' }}
                            >
                                Delete block
                            </button>
                        )}
                        <button type="button" className={styles.buttonSecondary} onClick={requestClose} disabled={pending}>
                            {isEditMode ? 'Done' : 'Cancel'}
                        </button>
                        {!isEditMode && (
                            <button
                                type="button"
                                className={styles.buttonPrimary}
                                onClick={handleCreateBlock}
                                disabled={pending}
                            >
                                {pending ? 'Creating…' : 'Create block'}
                            </button>
                        )}
                    </div>
                )}
            </aside>

            <ConfirmDestructiveDialog
                open={showDeleteConfirm}
                title="Delete block?"
                message={`Delete "${displayBlockName || 'this block'}"? Slides assigned to it may lose their block mapping.`}
                confirmLabel="Delete"
                onConfirm={handleDelete}
                onCancel={() => setShowDeleteConfirm(false)}
            />

            <ConfirmDestructiveDialog
                open={showDiscardNameConfirm}
                title="Discard block name change?"
                message={`You renamed this block to "${(name || '').trim() || '…'}" but have not saved. Discard the new name and close?`}
                confirmLabel="Discard"
                cancelLabel="Keep editing"
                onConfirm={handleDiscardNameAndClose}
                onCancel={() => setShowDiscardNameConfirm(false)}
            />

            <ConfirmDestructiveDialog
                open={!!showDiscardRegionConfirm}
                title="Discard region changes?"
                message="You have unsaved region details. Discard them?"
                confirmLabel="Discard"
                cancelLabel="Keep editing"
                onConfirm={confirmDiscardRegion}
                onCancel={() => setShowDiscardRegionConfirm(null)}
            />

            <ConfirmDestructiveDialog
                open={!!pendingRemove}
                title="Remove region?"
                message={
                    caseScoped
                        ? `Remove this region from case block "${displayBlockName}"? ${block?.itemCount || 0} slide(s) use this block.`
                        : `Remove this region from block "${displayBlockName}"?`
                }
                confirmLabel="Remove"
                onConfirm={() => pendingRemove && removeRegionFromBlock(pendingRemove)}
                onCancel={() => setPendingRemove(null)}
            />
        </>
    );
}
